import { useLanguage } from "@/context/language"
import { companyEmployeesCsv } from "@/cowork/company-employees-csv"
import { DialogCoworkDeleteEmployee } from "@/pages/home/cowork-project-dialogs"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import {
  CoworkApiError,
  createCompanyEmployee,
  deleteCompanyEmployee,
  listCompanyEmployees,
  updateCompanyEmployee,
  type CoworkCompanyEmployee,
  type CoworkCompanyEmployeeInput,
} from "@/cowork/api"
import { createComputed, createMemo, createResource, createUniqueId, For, on, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function CoworkEmployeesView(props: { companyID: string; canManage: boolean }) {
  const language = useLanguage()
  const dialog = useDialog()
  const formID = createUniqueId()
  const scope = createMemo(() => ({ companyID: props.companyID, canManage: props.canManage }))
  const validCompany = () => !!props.companyID.trim() && props.companyID.trim() !== "ALL"
  let disposed = false
  let controller: AbortController | undefined
  const [state, setState] = createStore({
    search: "",
    formOpen: false,
    editingID: "",
    input: { first_name: "", last_name: "", personal_id: "" },
    saving: false,
    deleting: "",
    deleteError: 0,
    error: 0,
  })
  const busy = () => state.saving || !!state.deleting
  let deleteDialogOpen = false
  const closeDeleteDialog = () => {
    if (!deleteDialogOpen) return
    deleteDialogOpen = false
    dialog.close()
  }
  const resetForm = () =>
    setState({
      formOpen: false,
      editingID: "",
      input: { first_name: "", last_name: "", personal_id: "" },
      error: 0,
      deleteError: 0,
    })
  createComputed(
    on(scope, () => {
      controller?.abort()
      closeDeleteDialog()
      resetForm()
      setState({ search: "", saving: false, deleting: "" })
    }),
  )
  onCleanup(() => {
    disposed = true
    closeDeleteDialog()
    controller?.abort()
  })
  const isCurrent = (target: ReturnType<typeof scope>) =>
    !disposed && scope() === target && target.canManage && validCompany()
  const [employees, actions] = createResource(
    () => (props.canManage && validCompany() ? scope() : false),
    async (target) => {
      controller?.abort()
      controller = new AbortController()
      const companyID = target.companyID
      const items = await listCompanyEmployees(companyID, controller.signal)
      return { companyID, items, scope: target }
    },
  )
  const data = createMemo(() => {
    // Reading the resource directly can suspend or throw; never expose another scope's cached rows.
    if (!props.canManage || !validCompany() || employees.state !== "ready") return undefined
    const value = employees.latest
    return value?.companyID === props.companyID && value.scope === scope() ? value : undefined
  })
  const ready = () => !!data()
  const filtered = createMemo(() => {
    const query = state.search.trim().toLowerCase()
    return (data()?.items ?? []).filter(
      (employee) =>
        employee.company_id === props.companyID &&
        (!query ||
          `${employee.first_name} ${employee.last_name} ${employee.personal_id}`.toLowerCase().includes(query)),
    )
  })
  const message = (status: number, action: "load" | "save" | "delete") => {
    if (status === 409 && action === "save") return language.t("cowork.enterprise.employees.duplicateID")
    if (status === 422 && action === "save") return language.t("cowork.enterprise.employees.invalidFields")
    if (status === 403) return language.t("cowork.enterprise.employees.denied")
    if (status === 404) return language.t("cowork.enterprise.employees.notFound")
    return language.t(
      action === "delete"
        ? "cowork.enterprise.employees.deleteError"
        : action === "save"
          ? "cowork.enterprise.employees.saveError"
          : "cowork.enterprise.employees.loadError",
    )
  }
  const validInput = () => Object.values(state.input).every((value) => !!value.trim() && value.trim().length <= 128)
  const downloadCsv = () => {
    if (!isCurrent(scope()) || !ready() || busy() || state.error === 403 || state.deleteError === 403) return
    const items = filtered()
    if (!items.length) return
    const csv = companyEmployeesCsv(items, [
      language.t("cowork.enterprise.employees.firstName"),
      language.t("cowork.enterprise.employees.lastName"),
      language.t("cowork.enterprise.employees.personalId"),
    ])
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = url
    link.download = `${language.t("cowork.enterprise.employees.title")}.csv`
    document.body.append(link)
    try {
      link.click()
    } finally {
      link.remove()
      URL.revokeObjectURL(url)
    }
  }
  const openForm = (employee?: CoworkCompanyEmployee) => {
    if (!isCurrent(scope()) || !ready() || busy()) return
    if (employee && employee.company_id !== props.companyID) return
    resetForm()
    setState({
      formOpen: true,
      editingID: employee?.id ?? "",
      input: {
        first_name: employee?.first_name ?? "",
        last_name: employee?.last_name ?? "",
        personal_id: employee?.personal_id ?? "",
      },
    })
  }
  const save = async (event: SubmitEvent) => {
    event.preventDefault()
    const target = scope()
    if (!isCurrent(target) || !ready() || !state.formOpen || busy()) return
    if (!validInput()) {
      setState("error", 422)
      return
    }
    const companyID = target.companyID
    const employeeID = state.editingID
    const input: CoworkCompanyEmployeeInput = {
      first_name: state.input.first_name.trim(),
      last_name: state.input.last_name.trim(),
      personal_id: state.input.personal_id.trim(),
    }
    setState({ saving: true, error: 0 })
    try {
      const employee = employeeID
        ? await updateCompanyEmployee(companyID, employeeID, input)
        : await createCompanyEmployee(companyID, input)
      // Identity also rejects an old save after switching away and back to the same company.
      if (!isCurrent(target)) return
      if (employee.company_id !== companyID) {
        setState("error", -1)
        return
      }
      actions.mutate((current) => {
        if (current?.scope !== target || current.companyID !== companyID) return current
        const existing = current.items.some((item) => item.id === employee.id || item.id === employeeID)
        return {
          ...current,
          items: existing
            ? current.items.map((item) => (item.id === employee.id || item.id === employeeID ? employee : item))
            : [...current.items, employee],
        }
      })
      resetForm()
    } catch (error) {
      if (isCurrent(target)) setState("error", error instanceof CoworkApiError ? error.status : -1)
    } finally {
      if (isCurrent(target)) setState("saving", false)
    }
  }

  const remove = async (employee: CoworkCompanyEmployee, target: ReturnType<typeof scope>) => {
    if (!isCurrent(target) || !ready() || busy() || employee.company_id !== target.companyID) return
    setState({ deleting: employee.id, deleteError: 0 })
    try {
      await deleteCompanyEmployee(target.companyID, employee.id)
      if (!isCurrent(target)) return
      actions.mutate((current) =>
        current?.scope === target
          ? { ...current, items: current.items.filter((item) => item.id !== employee.id) }
          : current,
      )
      if (state.editingID === employee.id) resetForm()
    } catch (error) {
      if (isCurrent(target)) setState("deleteError", error instanceof CoworkApiError ? error.status : -1)
    } finally {
      if (isCurrent(target)) setState("deleting", "")
    }
  }

  const confirmDelete = (employee: CoworkCompanyEmployee) => {
    const target = scope()
    if (!isCurrent(target) || !ready() || busy() || deleteDialogOpen || employee.company_id !== target.companyID) return
    deleteDialogOpen = true
    dialog.show(() => {
      onCleanup(() => {
        deleteDialogOpen = false
      })
      return (
        <DialogCoworkDeleteEmployee
          name={`${employee.first_name} ${employee.last_name}`}
          onDelete={() => void remove(employee, target)}
        />
      )
    })
  }

  return (
    <Show
      when={props.canManage && state.error !== 403 && state.deleteError !== 403}
      fallback={
        <p role="alert" class="text-[12px] text-v2-state-fg-danger">
          {language.t("cowork.enterprise.employees.denied")}
        </p>
      }
    >
      <div class="w-full min-w-0">
        <header class="flex min-w-0 flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div class="min-w-0">
            <h2 class="text-[15px] font-semibold">{language.t("cowork.enterprise.employees.title")}</h2>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.employees.description")}
            </p>
          </div>
          <div class="flex w-full min-w-0 gap-2 sm:w-auto sm:max-w-full">
            <label class="flex h-10 min-w-0 flex-1 items-center rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 sm:w-[260px]">
              <input
                type="text"
                autocomplete="off"
                class="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-v2-text-text-faint"
                aria-label={language.t("cowork.enterprise.employees.search")}
                placeholder={language.t("cowork.enterprise.employees.search")}
                value={state.search}
                onInput={(event) => setState("search", event.currentTarget.value)}
              />
            </label>
            <IconButtonV2
              type="button"
              size="large"
              variant="ghost"
              class="h-10 w-10 shrink-0"
              icon={<IconV2 name="download" size="small" />}
              aria-label={language.t("cowork.enterprise.employees.downloadCsv")}
              title={language.t("cowork.enterprise.employees.downloadCsv")}
              disabled={!ready() || busy() || filtered().length === 0}
              onClick={downloadCsv}
            />
            <button
              type="button"
              class="h-10 shrink-0 whitespace-nowrap rounded-lg bg-v2-background-bg-contrast px-4 py-2 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
              disabled={!ready() || busy()}
              onClick={() => openForm()}
            >
              {language.t("cowork.enterprise.employees.add")}
            </button>
          </div>
        </header>

        <Show when={state.formOpen && validCompany()}>
          <form
            class="mt-6 grid min-w-0 gap-3 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 md:grid-cols-3"
            aria-labelledby={`${formID}-heading`}
            aria-busy={busy()}
            noValidate
            onSubmit={save}
          >
            <h3 id={`${formID}-heading`} class="text-[13px] font-semibold md:col-span-3">
              {language.t(state.editingID ? "cowork.enterprise.employees.edit" : "cowork.enterprise.employees.create")}
            </h3>
            <For
              each={
                [
                  ["first_name", "cowork.enterprise.employees.firstName"],
                  ["last_name", "cowork.enterprise.employees.lastName"],
                  ["personal_id", "cowork.enterprise.employees.personalId"],
                ] as const
              }
            >
              {([field, key]) => (
                <div class="min-w-0">
                  <label for={`${formID}-${field}`} class="mb-1.5 block text-[12px] text-v2-text-text-muted">
                    {language.t(key)}
                  </label>
                  <input
                    id={`${formID}-${field}`}
                    type="text"
                    required
                    maxLength={128}
                    autocomplete="off"
                    autocapitalize="off"
                    spellcheck={false}
                    readOnly={busy()}
                    class="h-10 w-full min-w-0 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
                    value={state.input[field]}
                    onInput={(event) => {
                      if (!busy()) {
                        setState("input", field, event.currentTarget.value)
                        setState("error", 0)
                      }
                    }}
                  />
                </div>
              )}
            </For>
            <Show when={state.error}>
              <p role="alert" class="text-[12px] text-v2-state-fg-danger md:col-span-3">
                {message(state.error, "save")}
              </p>
            </Show>
            <div class="flex justify-end gap-2 md:col-span-3">
              <button
                type="button"
                class="rounded-md px-3 py-2 text-[11px] disabled:opacity-50"
                disabled={busy()}
                onClick={() => {
                  if (!busy()) resetForm()
                }}
              >
                {language.t("common.cancel")}
              </button>
              <button
                type="submit"
                class="rounded-md bg-v2-background-bg-contrast px-4 py-2 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                disabled={!ready() || busy() || !validInput()}
              >
                {state.saving ? language.t("cowork.enterprise.settings.saving") : language.t("common.save")}
              </button>
            </div>
          </form>
        </Show>

        <Show when={state.deleteError}>
          <p role="alert" class="mt-4 text-[12px] text-v2-state-fg-danger">
            {message(state.deleteError, "delete")}
          </p>
        </Show>
        <section
          aria-busy={!!state.deleting}
          class="mt-6 min-w-0 overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base"
        >
          <Show
            when={validCompany()}
            fallback={
              <p role="alert" class="p-5 text-[12px] text-v2-state-fg-danger">
                {language.t("cowork.enterprise.employees.notFound")}
              </p>
            }
          >
            <Show
              when={employees.state !== "errored"}
              fallback={
                <div class="p-5">
                  <p role="alert" class="text-[12px] text-v2-state-fg-danger">
                    {message(employees.error instanceof CoworkApiError ? employees.error.status : -1, "load")}
                  </p>
                  <button
                    type="button"
                    class="mt-3 rounded-md border border-v2-border-border-base px-3 py-2 text-[11px]"
                    onClick={() => {
                      if (isCurrent(scope()) && !busy() && employees.state === "errored") void actions.refetch()
                    }}
                  >
                    {language.t("cowork.enterprise.scope.retry")}
                  </button>
                </div>
              }
            >
              <Show
                when={ready()}
                fallback={
                  <p role="status" class="p-8 text-center text-[12px] text-v2-text-text-muted">
                    {language.t("common.loading")}
                  </p>
                }
              >
                <Show
                  when={filtered().length > 0}
                  fallback={
                    <p role="status" class="p-8 text-center text-[12px] text-v2-text-text-muted">
                      {language.t(
                        state.search.trim()
                          ? "cowork.enterprise.employees.noMatches"
                          : "cowork.enterprise.employees.empty",
                      )}
                    </p>
                  }
                >
                  <div class="relative min-w-0 overflow-x-auto">
                    <table class="w-full min-w-[600px] border-collapse text-left">
                      <thead>
                        <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                          <th scope="col" class="px-5 py-3">
                            {language.t("cowork.enterprise.employees.firstName")}
                          </th>
                          <th scope="col" class="px-5 py-3">
                            {language.t("cowork.enterprise.employees.lastName")}
                          </th>
                          <th scope="col" class="px-5 py-3">
                            {language.t("cowork.enterprise.employees.personalId")}
                          </th>
                          <th scope="col" class="px-5 py-3">
                            <span class="sr-only">{language.t("cowork.enterprise.employees.actions")}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={filtered()}>
                          {(employee) => (
                            <tr class="border-b border-v2-border-border-muted text-[12px] last:border-0 hover:bg-v2-overlay-simple-overlay-hover">
                              <td class="max-w-xs break-words px-5 py-4">{employee.first_name}</td>
                              <td class="max-w-xs break-words px-5 py-4">{employee.last_name}</td>
                              <td class="max-w-xs break-words px-5 py-4">{employee.personal_id}</td>
                              <td class="px-5 py-4 text-right">
                                <div class="flex justify-end gap-2">
                                  <IconButtonV2
                                    size="small"
                                    variant="ghost"
                                    icon={<IconV2 name="edit" size="small" />}
                                    aria-label={language.t("common.edit")}
                                    title={language.t("common.edit")}
                                    disabled={!ready() || busy()}
                                    onClick={() => openForm(employee)}
                                  />
                                  <IconButtonV2
                                    size="small"
                                    variant="ghost"
                                    class="text-v2-state-fg-danger"
                                    icon={<IconV2 name="trash" size="small" />}
                                    aria-label={language.t("common.delete")}
                                    title={language.t("common.delete")}
                                    disabled={!ready() || busy()}
                                    aria-busy={state.deleting === employee.id}
                                    onClick={() => confirmDelete(employee)}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </Show>
              </Show>
            </Show>
          </Show>
        </section>
      </div>
    </Show>
  )
}
